import { useState, useEffect } from "preact/hooks";
import { isMobileScreen, getVendorCost, getVendorCostCategory } from "../../helpers";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { VendorTile } from "../VendorTile";
import { render } from 'preact-render-to-string';

import style from "./Map.css";
import mapStyles from "./MapStyles.json"

const Map = ({ initialCoordinates, vendors, onIdle, onSelectPin, onFavouriteVendor }) => {
  const [mapLoaded, setMapLoaded] = useState(
    typeof window !== "undefined" && window.mapsScriptLoaded,
  );
  const [mapMarkers, setMapMarkers] = useState([]);

  function loadScript(url, callback) {
    let script = document.createElement("script");

    script.type = "text/javascript";
    if (script.readyState) {
      // only required for IE <9
      script.onreadystatechange = function () {
        if (
          script.readyState === "loaded" ||
          script.readyState === "complete"
        ) {
          script.onreadystatechange = null;
          callback();
        }
      };
    } else {
      script.onload = function () {
        callback();
      };
    }

    script.src = url;
    document.getElementsByTagName("head")[0].appendChild(script);
  }

  var infoWindow;
  var currentMarker;
  var idleListener;
  var zoomListener;

  useEffect(() => {
    if (!mapLoaded) {
      loadScript(
        "https://maps.googleapis.com/maps/api/js?key=AIzaSyB1oyZUjALeUEe2Y0HdMXPZ9k4UFO925MA&libraries=places,geometry&region=DE",
        () => {
          window.mapsScriptLoaded = true;
          window.globalMap = new google.maps.Map(document.getElementById("map"), {
            center: {
              lat: Number(initialCoordinates.lat) || 53,
              lng: Number(initialCoordinates.lng) || 13, //defaulting to berlin if no guessed location
            },
            zoom: Number(initialCoordinates.zoom) || 10,
            maxZoom: 15,
            disableDefaultUI: true,
            zoomControl: !isMobileScreen(),
            gestureHandling: "greedy",
            styles: mapStyles
          });

          setMapLoaded(true);
        },
      );
    }
  }, []);

  useEffect(() => {
    if (window.reinitializeMap) {
      window.globalMap = new google.maps.Map(document.getElementById("map"), {
        center: {
          lat: Number(initialCoordinates.lat) || 53,
          lng: Number(initialCoordinates.lng) || 13, //defaulting to berlin if no guessed location
        },
        zoom: Number(initialCoordinates.zoom) || 10,
        maxZoom: 15,
        disableDefaultUI: true,
        zoomControl: !isMobileScreen(),
        gestureHandling: "greedy",
        styles: mapStyles
      });
      window.reinitializeMap = false;
    }
  }, [])

  const handleMapIdle = () => {
    onIdle({
      lat: window.globalMap.center.lat(),
      lng: window.globalMap.center.lng(),
      zoom: window.globalMap.zoom
    })
  }

  const handleMapZoom = () => {
    if (infoWindow) {
      infoWindow.close();
    }
  }

  useEffect(() => {
    if (!mapLoaded) return;
    if (document.getElementById("map")) {
      if (mapMarkers.length) {
        for (let i = 0; i < mapMarkers.length; i++) {
          window.markerClusterer.removeMarker(mapMarkers[i])
        }
      }

      const assetPrefix = window.location.href.indexOf("localhost") > 0 ? "" : "search/";
      const whiteClusterPinUrl = assetPrefix + "assets/icons/pin-cluster-white.svg";
      const greenClusterPinUrl = assetPrefix + "assets/icons/pin-cluster-green.svg";

      idleListener = window.globalMap.addListener('idle', handleMapIdle);
      zoomListener = window.globalMap.addListener('zoom_changed', handleMapZoom);

      const markers = vendors?.map((vendor, index) => {
        const normalPin = {
          url: `${assetPrefix}assets/icons/pin-${vendor.isTop ? 'premium' : 'normal'}.svg`
        };
        const selectedPin = {
          url: `${assetPrefix}assets/icons/pin-${vendor.isTop ? 'premium-' : ''}selected.svg`,
          anchor: {
            x: 14,
            y: 24
          }
        }

        const marker = new google.maps.Marker({
          position: vendor.location,
          icon: normalPin,
          zIndex: index,
          id: index
        });

        vendor.highlightMarker = function () {
          const vendorCluster = window.markerClusterer?.clusters.find(cluster => cluster.markers.some(m => m.id == marker.id));
          if (vendorCluster && vendorCluster.markers.length > 1) {
            const markerSize = vendorCluster.markers.length > 50 ? 80 : vendorCluster.markers.length > 10 ? 56 : 40;
            setPinAndSelect(vendorCluster.marker, { url: greenClusterPinUrl, scaledSize: new google.maps.Size(markerSize, markerSize) });
          } else {
            setPinAndSelect(marker, selectedPin);
          }
        };

        vendor.stopBounce = function () {
          const vendorCluster = window.markerClusterer?.clusters.find(cluster => cluster.markers.some(m => m.id == marker.id));
          if (vendorCluster && vendorCluster.markers.length > 1) {
            const markerSize = vendorCluster.markers.length > 50 ? 80 : vendorCluster.markers.length > 10 ? 56 : 40;
            setPinAndUnselect(vendorCluster.marker, { url: whiteClusterPinUrl, scaledSize: new google.maps.Size(markerSize, markerSize) });
          } else if (currentMarker != marker) {
            setPinAndUnselect(marker, normalPin);
          }
        };

        marker.addListener("mouseover", () => {
          setPinAndSelect(marker, selectedPin);
        });

        marker.addListener("mouseout", () => {
          if (currentMarker != marker) {
            setPinAndUnselect(marker, normalPin);
          }
        });

        marker.addListener("click", () => {
          if (infoWindow) {
            infoWindow.close();
          }

          if (!currentMarker) {
            currentMarker = marker;
            setPinAndSelect(marker, selectedPin);
          } else if (currentMarker != marker) {
            setPinAndUnselect(currentMarker, normalPin);
            setPinAndSelect(marker, selectedPin);
            currentMarker = marker;
          }

          onSelectPin(vendor);

          if (isMobileScreen()) return;

          const contentString = render(
            <div class={style.vendorTileInfoWrapper}>
              <VendorTile
                key={vendor.id}
                isHorizontal={true}
                vendor={vendor}
                onFavouriteVendor={onFavouriteVendor}
                tileId={`${vendor.id}-infoWindow`}
                isCompact={true}
              />
            </div>
          )

          infoWindow = new google.maps.InfoWindow({
            content: contentString,
          });

          google.maps.event.addListener(infoWindow, 'domready', () => {
            // TODO: This event listener can potentially be used to change the content without creating a new infoWindow on different pin clicks
            // https://stackoverflow.com/questions/11350234/google-maps-infowindow-events-on-open
            new Carousel({
              init: true,
              vendorId: vendor.id,
              element: `carousel_${vendor.id}-infoWindow`, // id of the carousel container
              autoplay: false, // starts the rotation automatically
              infinite: false, // enables infinite scrolling (last index -> first)
              interval: 1500, // interval between slide changes
              initial: 0, // slide to start with
              dots: true, // show navigation dots
              arrows: true, // show navigation arrows
              buttons: false, // hide <play>/<stop> buttons,
              btnStopText: "Pause", // <stop> button text
            });
            if (window.lazyLoadInstance) window.lazyLoadInstance.update();
            if (window.lazyLoadVendorInstance) window.lazyLoadVendorInstance.update();

            const classRegex = /class="(.+?)"/;
            const infoWindowClass = classRegex.exec(infoWindow.getContent())[1];
            const infoWindowNode = document.getElementsByClassName(infoWindowClass)[0];
            const favouritesButton = infoWindowNode.querySelector("#favouritesButton");
            const vendorTileContent = infoWindowNode.querySelector("#vendorTileContent");

            favouritesButton.onclick = (e) => {
              e.preventDefault();
              if (vendor.favourited) {
                return;
              }
              onFavouriteVendor(vendor);
            }

            vendorTileContent.onclick = (e) => {
              e.preventDefault();
              const searchUrl = new URL(window.location.href);
              searchUrl.hash = vendor.id;
              window.history.replaceState({}, '', searchUrl.toString());
              window.location.href = `/vendors/${vendor.slug}`;
            }
          });

          google.maps.event.addListener(infoWindow, 'closeclick', () => {
            currentMarker.setIcon(normalPin);
            currentMarker = null;
          });

          infoWindow.open({
            anchor: marker,
            shouldFocus: true
          });
        });

        return marker;
      });

      setMapMarkers(markers);

      const clusterRenderer = {
        render({ count, position }) {
          const markerSize = count > 50 ? 80 : count > 10 ? 56 : 40;

          const whiteClusterIcon = {
            url: whiteClusterPinUrl,
            scaledSize: new google.maps.Size(markerSize, markerSize),
          };

          const greenClusterIcon = {
            url: greenClusterPinUrl,
            scaledSize: new google.maps.Size(markerSize, markerSize),
          };

          const clusterMaker = new google.maps.Marker({
            position,
            icon: whiteClusterIcon,
            label: {
              text: String(count),
              color: "rgba(0,0,0)",
              fontSize: "16px",
            },
            // adjust zIndex to be above other markers
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          });

          // Desktop cluster listeners
          clusterMaker.addListener("mouseover", () => {
            clusterMaker.setIcon(greenClusterIcon);
            const clusterMakerLabel = clusterMaker.getLabel();
            clusterMakerLabel.color = "white";
            clusterMaker.setLabel(clusterMakerLabel);
            clusterMaker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
          });

          clusterMaker.addListener("mouseout", () => {
            clusterMaker.setIcon(whiteClusterIcon);
            const markerLabel = clusterMaker.getLabel();
            markerLabel.color = "black";
            clusterMaker.setLabel(markerLabel);
            clusterMaker.setZIndex(undefined);
          });

          return clusterMaker;
        },
      };

      const customSuperClusterAlgorithm = new SuperClusterAlgorithm({ radius: 100, minPoints: 2, maxZoom: 14 })

      window.markerClusterer = new MarkerClusterer({ map: window.globalMap, markers, renderer: clusterRenderer, algorithm: customSuperClusterAlgorithm });

      window.globalMap.setZoom(Number(initialCoordinates.zoom) || 10)
      window.globalMap
        .setCenter({
          lat: Number(initialCoordinates.lat) || 53,
          lng: Number(initialCoordinates.lng) || 13,
        });

      return () => {
        google.maps.event.removeListener(idleListener);
        google.maps.event.removeListener(zoomListener);
      }
    }
  }, [mapLoaded, vendors]);

  const setPinAndSelect = (marker, greenPin) => {
    marker.setIcon(greenPin);
    const markerLabel = marker.getLabel();
    if (markerLabel) {
      markerLabel.color = "white";
      marker.setLabel(markerLabel);
    }
    marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
  };

  const setPinAndUnselect = (marker, whitePin) => {
    marker.setIcon(whitePin);
    const markerLabel = marker.getLabel();
    if (markerLabel) {
      markerLabel.color = "black";
      marker.setLabel(markerLabel);
    }
    marker.setZIndex(undefined);
  };

  return <div class={style.map} id="map" />;
};

export { Map };
